/**
 * T267 — Career stats display block.
 *
 * Renders the {batting, bowling} rollup the backend returns on
 * /find-a-player/<id>/ and /api/coredata/player-profile/me/.
 *
 * Hides if both batting.matches and bowling.matches are 0 — empty
 * stat block doesn't help anyone.
 */
import { BarChart3, Target, Trophy } from "lucide-react";


function StatTile({ label, value, suffix = "" }) {
  return (
    <div className="text-center px-2 py-1">
      <div className="text-lg font-bold text-text-primary">
        {value}{suffix && <span className="text-sm font-normal text-text-secondary">{suffix}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-text-secondary">{label}</div>
    </div>
  );
}


export default function CareerStatsBlock({ stats }) {
  if (!stats) return null;
  const bat = stats.batting || {};
  const bow = stats.bowling || {};
  const hasBatting = (bat.matches || 0) > 0;
  const hasBowling = (bow.matches || 0) > 0;
  if (!hasBatting && !hasBowling) return null;

  return (
    <div className="border border-bg-secondary rounded-lg p-4 bg-bg-primary mb-4">
      <h2 className="font-semibold mb-3 flex items-center gap-2 text-text-primary">
        <Trophy className="w-4 h-4 text-amber-400" />
        Career on ZenCricketData
      </h2>

      {hasBatting && (
        <div className="mb-3">
          <div className="flex items-center gap-1 text-sm font-medium text-text-secondary mb-1">
            <BarChart3 className="w-3.5 h-3.5" /> Batting
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 border border-bg-secondary rounded bg-bg-main">
            <StatTile label="Matches" value={bat.matches} />
            <StatTile label="Innings" value={bat.innings} />
            <StatTile label="Runs"    value={bat.runs} />
            <StatTile label="Average" value={bat.average?.toFixed?.(2) || bat.average} />
            <StatTile label="SR"      value={bat.strike_rate?.toFixed?.(2) || bat.strike_rate} />
            <StatTile label="HS"      value={bat.highest_score} suffix={bat.not_outs > 0 ? "*" : ""} />
          </div>
          <p className="text-[10px] text-text-secondary mt-1">
            {bat.fours} fours · {bat.sixes} sixes · {bat.not_outs} not outs
          </p>
        </div>
      )}

      {hasBowling && (
        <div>
          <div className="flex items-center gap-1 text-sm font-medium text-text-secondary mb-1">
            <Target className="w-3.5 h-3.5" /> Bowling
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 border border-bg-secondary rounded bg-bg-main">
            <StatTile label="Matches" value={bow.matches} />
            <StatTile label="Overs"   value={bow.overs} />
            <StatTile label="Wickets" value={bow.wickets} />
            <StatTile label="Econ"    value={bow.economy?.toFixed?.(2) || bow.economy} />
            <StatTile label="Avg"     value={bow.average?.toFixed?.(2) || bow.average} />
            <StatTile label="SR"      value={bow.strike_rate?.toFixed?.(2) || bow.strike_rate} />
          </div>
          <p className="text-[10px] text-text-secondary mt-1">
            {bow.maidens} maidens · {bow.wides} wides · {bow.no_balls} no-balls
          </p>
        </div>
      )}

      <p className="text-[10px] text-text-secondary mt-3 italic">
        Stats aggregated from matches coded on ZenCricketData. Numbers
        update as scorers record new innings.
      </p>
    </div>
  );
}
